/**
 * VireoHR - Owner Employees Screen
 * Full CRUD: Add/edit employees, assign to locations, set salary
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployees, Employee } from '@/hooks/useEmployees';
import { firestore } from '@/lib/firebase/client';
import { createEmployee, updateEmployee } from '@/services/cloudFunctions';


interface Location {
  id: string;
  name: string;
}

export default function Employees() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Use shared hook for employee data
  const { employees, loading, refetch } = useEmployees({
    tenantId: user?.tenantId || '',
    role: 'owner'
  });
  
  const [locations, setLocations] = useState<Location[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    salaryType: 'hourly' as 'hourly' | 'salary',
    hourlyRate: '',
    monthlySalary: '',
    // PRD v4.1: expectedHours removed - Super Admin only, never exposed to Owners
    locationIds: [] as string[],
    isManager: false,
    managerLocationIds: [] as string[],
  });

  // Fetch locations separately (not in the shared hook)
  useEffect(() => {
    const fetchLocations = async () => {
      if (!user?.tenantId) return;
      
      try {
        const branchesRef = collection(firestore, 'branches');
        const branchesQuery = query(
          branchesRef,
          where('tenantId', '==', user.tenantId)
        );
        const branchesSnapshot = await getDocs(branchesQuery);
        const locationsData = branchesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        })) as Location[];
        
        setLocations(locationsData);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };
    
    fetchLocations();
  }, [user?.tenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.tenantId) return;
    
    try {
      const loadingToast = toast.loading('Creating employee...');
      
      const result = await createEmployee({
        tenantId: user.tenantId,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        salaryType: formData.salaryType,
        hourlyRate: formData.salaryType === 'hourly' ? parseFloat(formData.hourlyRate) : undefined,
        monthlySalary: formData.salaryType === 'salary' ? parseFloat(formData.monthlySalary) : undefined,
        // PRD v4.1: expectedHours removed - Super Admin controls this, not Owner
        locationIds: formData.locationIds,
        isManager: formData.isManager,
        managerLocationIds: formData.isManager ? formData.managerLocationIds : undefined,
      });
      
      toast.dismiss(loadingToast);
      
      if (result.data.success) {
        toast.success(`Employee created! Default password: ${result.data.defaultPassword}`);
        setShowAddForm(false);
        resetForm();
        refetch();
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to create employee');
    }
  };
  
  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      phone: employee.phone || '',
      salaryType: employee.salaryType,
      hourlyRate: employee.hourlyRate?.toString() || '',
      monthlySalary: employee.monthlySalary?.toString() || '',
      // PRD v4.1: expectedHours removed - Super Admin only
      locationIds: employee.locationIds || [],
      isManager: employee.isManager,
      managerLocationIds: employee.managerLocationIds || [],
    });
    setShowAddForm(true);
  };
  
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingEmployee) return;
    
    try {
      const loadingToast = toast.loading('Updating employee...');
      
      await updateEmployee({
        employeeId: editingEmployee.id,
        updates: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          salaryType: formData.salaryType,
          hourlyRate: formData.salaryType === 'hourly' ? parseFloat(formData.hourlyRate) : undefined,
          monthlySalary: formData.salaryType === 'salary' ? parseFloat(formData.monthlySalary) : undefined,
          // PRD v4.1: expectedHours removed - Super Admin controls this
          locationIds: formData.locationIds,
          isManager: formData.isManager,
          managerLocationIds: formData.isManager ? formData.managerLocationIds : undefined,
        },
      });
      
      toast.dismiss(loadingToast);
      toast.success('Employee updated successfully');
      setShowAddForm(false);
      setEditingEmployee(null);
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update employee');
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      salaryType: 'hourly',
      hourlyRate: '',
      monthlySalary: '',
      // PRD v4.1: expectedHours removed - Super Admin only
      locationIds: [],
      isManager: false,
      managerLocationIds: [],
    });
  };

  if (loading) {
    return (
      <Layout title="Employees">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-vireo-teal border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Employees">
      <div className="p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-vireo-teal">Employees</h1>
          <Button
            onClick={() => {
              setEditingEmployee(null);
              resetForm();
              setShowAddForm(true);
            }}
            className="bg-vireo-teal hover:bg-vireo-teal/90"
          >
            ➕ Add Employee
          </Button>
        </div>

        {/* Employee List */}
        <div className="space-y-3">
          {employees.map((employee) => (
            <Card key={employee.id} className=" transition">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-vireo-teal">{employee.name}</h3>
                    {employee.isManager && (
                      <Badge variant="info">Manager</Badge>
                    )}
                    {!employee.active && (
                      <Badge variant="error">Inactive</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{employee.email}</p>
                  {employee.phone && (
                    <p className="text-xs text-gray-500">{employee.phone}</p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    {employee.salaryType === 'hourly' ? (
                      <div>
                        <p className="font-bold text-vireo-gold">{employee.hourlyRate} JD/hr</p>
                        <p className="text-xs text-gray-500">Hourly</p>
                      </div>
                    ) : (
                      <div>
                        <p className="font-bold text-vireo-gold">{employee.monthlySalary} JD</p>
                        <p className="text-xs text-gray-500">Monthly</p>
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={() => handleEdit(employee)}
                    variant="secondary"
                    className="text-sm"
                  >
                    Edit
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {employees.length === 0 && (
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <p className="text-gray-500">No employees yet</p>
              <p className="text-sm text-gray-400 mt-2">Add your first employee to get started</p>
            </div>
          </Card>
        )}
      </div>

      {/* Add/Edit Employee Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <Card className="max-w-2xl w-full my-8">
            <h2 className="text-xl font-bold text-vireo-teal mb-4">
              {editingEmployee ? 'Edit Employee' : 'Add Employee'}
            </h2>
            
            <form onSubmit={editingEmployee ? handleUpdate : handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vireo-teal focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vireo-teal focus:outline-none"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vireo-teal focus:outline-none"
                />
              </div>
              
              {/* Salary Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salary Type *</label>
                <select
                  value={formData.salaryType}
                  onChange={(e) => setFormData({ ...formData, salaryType: e.target.value as 'hourly' | 'salary' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vireo-teal focus:outline-none"
                >
                  <option value="hourly">Hourly Rate</option>
                  <option value="salary">Monthly Salary</option>
                </select>
              </div>
              
              {/* Salary Details */}
              {formData.salaryType === 'hourly' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate (JD) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.hourlyRate}
                    onChange={(e) => setFormData({ ...formData, hourlyRate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vireo-teal focus:outline-none"
                    required
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Salary (JD) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.monthlySalary}
                      onChange={(e) => setFormData({ ...formData, monthlySalary: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-vireo-teal focus:outline-none"
                      required
                    />
                  </div>
                  {/* PRD v4.1: expectedHours/expectedMonthlyHours removed from Owner UI */}
                  {/* Super Admin controls this value via tenant settings */}
                </div>
              )}
              
              {/* Manager Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isManager"
                  checked={formData.isManager}
                  onChange={(e) => setFormData({ ...formData, isManager: e.target.checked })}
                  className="w-4 h-4 text-vireo-teal border-gray-300 rounded focus:ring-vireo-teal"
                />
                <label htmlFor="isManager" className="text-sm font-medium text-gray-700">
                  Assign as Manager
                </label>
              </div>
              
              {/* Locations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Locations
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {locations.map((location) => (
                    <label key={location.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.locationIds.includes(location.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              locationIds: [...formData.locationIds, location.id]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              locationIds: formData.locationIds.filter(id => id !== location.id)
                            });
                          }
                        }}
                        className="w-4 h-4 text-vireo-teal border-gray-300 rounded focus:ring-vireo-teal"
                      />
                      <span className="text-sm text-gray-700">{location.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Manager Locations */}
              {formData.isManager && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manager for Locations
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {locations.map((location) => (
                      <label key={location.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.managerLocationIds.includes(location.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                managerLocationIds: [...formData.managerLocationIds, location.id]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                managerLocationIds: formData.managerLocationIds.filter(id => id !== location.id)
                              });
                            }
                          }}
                          className="w-4 h-4 text-vireo-teal border-gray-300 rounded focus:ring-vireo-teal"
                        />
                        <span className="text-sm text-gray-700">{location.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-vireo-teal hover:bg-vireo-teal/90">
                  {editingEmployee ? 'Update Employee' : 'Create Employee'}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingEmployee(null);
                    resetForm();
                  }}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </Layout>
  );
}
